import type { DatabricksCatalogTreeResponse } from '@protoss/shared';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';

interface DataCatalogTreeProps {
  tree: DatabricksCatalogTreeResponse | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedCatalog: string | null;
  selectedSchema: string | null;
  selectedTable: string | null;
  onSelectTable: (catalog: string, schema: string, table: string) => void;
}

export function DataCatalogTree({
  tree,
  loading,
  error,
  onRetry,
  selectedCatalog,
  selectedSchema,
  selectedTable,
  onSelectTable,
}: DataCatalogTreeProps) {
  const [openSchemas, setOpenSchemas] = React.useState<Set<string>>(new Set());

  // Auto-open the first schema when tree loads for the first time
  const autoOpenDone = React.useRef(false);
  React.useEffect(() => {
    if (!tree || autoOpenDone.current) return;
    const firstSchema = tree.catalogs[0]?.schemas[0]?.name;
    if (firstSchema) {
      setOpenSchemas(new Set([firstSchema]));
      autoOpenDone.current = true;
    }
  }, [tree]);

  function toggleSchema(schemaName: string) {
    setOpenSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(schemaName)) {
        next.delete(schemaName);
      } else {
        next.add(schemaName);
      }
      return next;
    });
  }

  if (loading) {
    return <div className="ss-browser__empty">Loading catalog…</div>;
  }

  if (error) {
    return <PageErrorState message="Failed to load catalog." onRetry={onRetry} />;
  }

  if (!tree || tree.catalogs.length === 0) {
    return <div className="ss-browser__empty">No schemas found.</div>;
  }

  const multipleCatalogs = tree.catalogs.length > 1;

  return (
    <div className="ss-browser-tree" role="tree" aria-label="Catalog contents">
      {tree.catalogs.map((catalog) => {
        const depthOffset = multipleCatalogs ? 1 : 0;
        return (
          <div key={catalog.catalogName} className="ss-browser-tree__branch">
            {multipleCatalogs && (
              <div className="dc-catalog-section">
                <span className="ss-browser__subtitle">{catalog.catalogName}</span>
              </div>
            )}
            {catalog.schemas.map((schema) => {
              const isOpen = openSchemas.has(schema.name);
              const tableDepth = depthOffset + 1;
              return (
                <div key={schema.name} className="ss-browser-tree__branch">
                  <div
                    className="ss-browser-tree__row"
                    style={{ '--ss-tree-depth': `${depthOffset}` } as React.CSSProperties}
                  >
                    <button
                      type="button"
                      className="ss-browser-tree__toggle"
                      onClick={() => toggleSchema(schema.name)}
                      aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${schema.name}`}
                    >
                      <svg
                        className="ss-browser-tree__chevron"
                        data-expanded={isOpen ? 'true' : 'false'}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 2l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="ss-browser-tree__item ss-browser-tree__item--directory"
                      role="treeitem"
                      aria-expanded={isOpen}
                      onClick={() => toggleSchema(schema.name)}
                    >
                      <span className="ss-browser-tree__icon">{isOpen ? '▣' : '▢'}</span>
                      <span className="ss-browser-tree__label">{schema.name}</span>
                      <span className="dc-schema-count">{schema.tables.length}</span>
                    </button>
                  </div>

                  {isOpen && (
                    <div className="ss-browser-tree__children">
                      {schema.tables.length === 0 ? (
                        <div
                          className="ss-browser-tree__row"
                          style={{ '--ss-tree-depth': `${tableDepth}` } as React.CSSProperties}
                        >
                          <button
                            type="button"
                            className="ss-browser-tree__toggle"
                            disabled
                            aria-hidden="true"
                          />
                          <span className="ss-browser-tree__item ss-browser-tree__item--file ss-browser-tree__item--empty">
                            No tables.
                          </span>
                        </div>
                      ) : (
                        schema.tables.map((tableName) => {
                          const isSelected =
                            selectedCatalog === catalog.catalogName &&
                            selectedSchema === schema.name &&
                            selectedTable === tableName;
                          return (
                            <div
                              key={tableName}
                              className={`ss-browser-tree__row${isSelected ? ' ss-browser-tree__row--selected' : ''}`}
                              style={{ '--ss-tree-depth': `${tableDepth}` } as React.CSSProperties}
                            >
                              <button
                                type="button"
                                className="ss-browser-tree__toggle"
                                disabled
                                aria-hidden="true"
                              />
                              <button
                                type="button"
                                className="ss-browser-tree__item ss-browser-tree__item--file"
                                role="treeitem"
                                aria-current={isSelected ? 'true' : undefined}
                                onClick={() =>
                                  onSelectTable(catalog.catalogName, schema.name, tableName)
                                }
                              >
                                <span className="ss-browser-tree__icon">≡</span>
                                <span className="ss-browser-tree__label">{tableName}</span>
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
